import { t, onLanguageChange } from './i18n.js';
import { createItemIconProvider, dimColor } from './pixel-icons.js';
import { makeEmptyStack, cloneStack, clearStack, isEmptyStack, getStackMax } from './ui/stack-utils.js';
import { findCraftResult } from './ui/crafting-matcher.js';
import { createCreativePalette } from './ui/creative-palette.js';

export function createInventoryUI({
  itemDefs,
  recipes,
  hotbarDefault,
  onCraft = null,
  onQuickMoveInventorySlot = null,
  onCollectSimilarExternal = null,
  onDropCursorOutside = null
}) {
  const HOTBAR_SIZE = 9;
  const MAIN_SIZE = 27;
  const TOTAL_SLOTS = HOTBAR_SIZE + MAIN_SIZE;
  const ARMOR_SLOT_ORDER = ['helmet', 'chestplate', 'leggings', 'boots'];
  const ARMOR_SLOT_SHORT = {
    helmet: 'H',
    chestplate: 'C',
    leggings: 'L',
    boots: 'B'
  };

  const inventory = {};
  for (const id of Object.keys(itemDefs)) inventory[id] = 0;

  const slots = Array.from({ length: TOTAL_SLOTS }, () => makeEmptyStack());
  const cursorStack = makeEmptyStack();

  const preferredHotbar = new Map();
  for (let i = 0; i < Math.min(hotbarDefault.length, HOTBAR_SIZE); i++) {
    preferredHotbar.set(hotbarDefault[i], i);
  }

  let activeSlotIndex = 0;
  let craftOpen = false;
  let tableAccess = false;
  let craftMode = 'hand';
  let creativeMode = false;

  const craftSlots = {
    hand: Array.from({ length: 4 }, () => makeEmptyStack()),
    table: Array.from({ length: 9 }, () => makeEmptyStack())
  };
  const equippedArmor = {
    helmet: makeEmptyStack(),
    chestplate: makeEmptyStack(),
    leggings: makeEmptyStack(),
    boots: makeEmptyStack()
  };

  let craftResult = null;

  const hotbarUI = document.getElementById('hotbar');
  const inventoryOverlay = document.getElementById('inventory-overlay');
  const inventoryWindow = document.getElementById('inventory-window');
  const inventoryTitle = document.getElementById('inventory-title');
  const tableHint = document.getElementById('inventory-table-hint');
  const modeToggleBtn = document.getElementById('craft-mode-toggle');
  const furnaceOverlay = document.getElementById('furnace-overlay');
  const furnaceWindow = document.getElementById('furnace-window');
  const chestOverlay = document.getElementById('chest-overlay');
  const chestWindow = document.getElementById('chest-window');
  const craftingGrid = document.getElementById('crafting-grid');
  const craftingResultSlot = document.getElementById('crafting-result-slot');
  const equipmentGrid = document.getElementById('equipment-grid');
  const equipmentLabelEl = document.getElementById('equipment-label');
  const inventoryMainGrid = document.getElementById('inventory-main-grid');
  const inventoryHotbarGrid = document.getElementById('inventory-hotbar-grid');
  const cursorEl = document.getElementById('inventory-cursor');
  if (cursorEl && cursorEl.parentElement !== document.body) {
    document.body.appendChild(cursorEl);
  }
  const tooltipEl = document.getElementById('inventory-tooltip');
  if (tooltipEl && tooltipEl.parentElement !== document.body) {
    document.body.appendChild(tooltipEl);
  }
  const inventoryTipEl = document.getElementById('inventory-tip');
  const inventoryMainLabelEl = document.getElementById('inventory-main-label');
  const inventoryHotbarLabelEl = document.getElementById('inventory-hotbar-label');
  const inventoryTrashLabelEl = document.getElementById('inventory-trash-label');
  const inventoryTrashSlotEl = document.getElementById('inventory-trash-slot');
  const inventorySortBtnEl = document.getElementById('inventory-sort-btn');

  function ensureSharedOverlayLayout() {
    if (!inventoryOverlay || !inventoryWindow) return;

    let workspace = document.getElementById('inventory-workspace');
    if (!workspace) {
      workspace = document.createElement('div');
      workspace.id = 'inventory-workspace';
      inventoryOverlay.prepend(workspace);
    }

    if (!document.getElementById('inventory-shared-layout-style')) {
      const style = document.createElement('style');
      style.id = 'inventory-shared-layout-style';
      style.textContent = `
#inventory-workspace {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 14px;
  width: min(1480px, 96vw);
  max-height: calc(100vh - 36px);
}

#inventory-overlay {
  padding: 18px;
  box-sizing: border-box;
  overflow: auto;
  align-items: center;
}

#inventory-window,
#chest-window,
#furnace-window {
  max-height: calc(100vh - 36px);
  overflow-y: auto;
  overflow-x: hidden;
}

#inventory-overlay #chest-overlay,
#inventory-overlay #furnace-overlay {
  position: static;
  inset: auto;
  display: none;
  justify-content: flex-start;
  align-items: stretch;
  padding: 0;
  background: transparent;
  z-index: auto;
  cursor: default;
  pointer-events: auto;
  flex: 0 0 auto;
}

#inventory-overlay #chest-window,
#inventory-overlay #furnace-window {
  margin: 0;
  height: 100%;
  box-sizing: border-box;
}

#inventory-overlay #chest-window {
  width: min(540px, 36vw);
}

#inventory-overlay #furnace-window {
  width: min(420px, 30vw);
}

body.chest-open #inventory-overlay,
body.furnace-open #inventory-overlay {
  justify-content: center;
  align-items: center;
  padding: 18px;
}

body.chest-open #inventory-overlay #chest-overlay,
body.furnace-open #inventory-overlay #furnace-overlay {
  display: flex;
}

body.chest-open #inventory-window {
  width: min(980px, 58vw);
}

body.furnace-open #inventory-window {
  width: min(980px, 60vw);
}

@media (max-width: 1180px) {
  #inventory-workspace {
    flex-direction: column;
    align-items: center;
    width: min(100%, 1040px);
  }

  #inventory-window,
  body.chest-open #inventory-window,
  body.furnace-open #inventory-window,
  #inventory-overlay #chest-window,
  #inventory-overlay #furnace-window {
    width: min(1060px, 94vw);
  }

  body.chest-open #inventory-overlay #chest-overlay,
  body.furnace-open #inventory-overlay #furnace-overlay {
    width: min(1060px, 94vw);
  }
}
`;
      document.head.appendChild(style);
    }

    if (inventoryWindow.parentElement !== workspace) workspace.appendChild(inventoryWindow);
    if (chestOverlay && chestOverlay.parentElement !== workspace) workspace.appendChild(chestOverlay);
    if (furnaceOverlay && furnaceOverlay.parentElement !== workspace) workspace.appendChild(furnaceOverlay);

    inventoryWindow.dataset.inventoryPanel = 'inventory';
    if (chestWindow) chestWindow.dataset.inventoryPanel = 'chest';
    if (furnaceWindow) furnaceWindow.dataset.inventoryPanel = 'furnace';
  }

  const slotElements = Array.from({ length: TOTAL_SLOTS }, () => []);
  const equipmentSlotEls = new Map();
  const craftInputEls = [];

  const dragPlace = {
    active: false,
    button: 0,
    itemId: null,
    visited: new Set()
  };
  const craftLeftDrag = {
    active: false,
    mode: 'hand',
    itemId: null,
    startIndex: -1,
    moved: false,
    visited: new Set(),
    baseCursor: makeEmptyStack(),
    baseSlots: []
  };

  const clickTracker = {
    itemId: null,
    time: 0,
    source: null
  };

  const iconProvider = createItemIconProvider(itemDefs);
  const creativePalette = createCreativePalette({
    itemDefs,
    iconProvider,
    getStackMax,
    isDurableItemDef,
    renderSingleSlot,
    bindTooltip,
    onMouseDown: handleCreativePaletteMouseDown
  });

  function syncInventoryCounts() {
    for (const id of Object.keys(inventory)) inventory[id] = 0;
    for (const slot of slots) {
      if (isEmptyStack(slot)) continue;
      inventory[slot.id] = (inventory[slot.id] || 0) + slot.count;
    }
  }

  function isDurableItemDef(def) {
    return !!(def && (def.kind === 'tool' || def.kind === 'armor') && Number(def.maxDurability) > 0);
  }

  function ensureToolDurability(slot) {
    if (isEmptyStack(slot)) return;
    const def = itemDefs[slot.id];
    if (!isDurableItemDef(def)) return;
    if (!slot.durability) slot.durability = def.maxDurability;
  }

  function setStack(slot, itemId, count, durability = null) {
    slot.id = itemId;
    slot.count = count;
    slot.durability = durability;
    ensureToolDurability(slot);
  }

  function canEquipToArmorSlot(itemId, slotName) {
    const def = itemDefs[itemId];
    return !!(def && def.kind === 'armor' && def.armorSlot === slotName);
  }

  function describeItem(def, stack) {
    if (!def) return '';
    if (def.desc) return def.desc;

    if (def.kind === 'tool') {
      const toolName = def.toolType === 'pickaxe'
        ? t('ui.tool.pickaxe')
        : def.toolType === 'axe'
          ? t('ui.tool.axe')
          : def.toolType === 'shovel'
            ? t('ui.tool.shovel')
            : def.toolType === 'hoe'
              ? t('ui.tool.hoe')
          : def.toolType === 'sword'
              ? t('ui.tool.sword')
              : def.toolType === 'bow'
              ? t('ui.tool.bow')
                : def.toolType === 'shield'
                  ? t('ui.tool.shield')
                : def.toolType === 'flint_steel'
                    ? t('ui.tool.flint_steel')
                    : def.toolType === 'shears'
                      ? t('ui.tool.shears')
                    : def.toolType === 'bucket'
                      ? t('ui.tool.bucket')
                    : t('ui.tool.shovel');
      const atk = def.attackDamage
        ? t('ui.tool.atk', { value: def.attackDamage.toFixed(1) })
        : '';
      const dura = stack && stack.durability
        ? t('ui.tool.durability', { value: stack.durability, max: def.maxDurability })
        : '';
      return `${toolName}${atk}${dura}`.trim();
    }

    if (def.kind === 'armor') {
      const slotName = t(`ui.armor.slot.${def.armorSlot}`);
      const dura = stack && stack.durability
        ? t('ui.tool.durability', { value: stack.durability, max: def.maxDurability }).trim()
        : '';
      return t('ui.armor.desc', {
        value: Number(def.armor || 0),
        slot: slotName,
        durability: dura ? ` ${dura}` : ''
      }).trim();
    }

    if (def.kind === 'food') {
      const nutrition = Number(def.nutrition || 0);
      const heal = Number(def.heal || 0);
      if (def.isMedicine) {
        return t('ui.medicine.desc', { heal });
      }
      return t('ui.food.desc', {
        nutrition,
        heal: heal > 0 ? t('ui.food.heal', { value: heal }) : ''
      });
    }

    if (def.kind === 'block') return t('ui.block.desc');
    if (def.kind === 'material') return t('ui.material.desc');
    return t('ui.item.desc');
  }

  function getItemDisplayName(def) {
    if (!def) return '';
    const key = `item.${def.id}`;
    const localized = t(key);
    return localized === key ? def.name : localized;
  }


  function showTooltip(stack, x, y) {
    if (!tooltipEl || isEmptyStack(stack)) {
      if (tooltipEl) tooltipEl.style.display = 'none';
      return;
    }

    const def = itemDefs[stack.id];
    if (!def) {
      tooltipEl.style.display = 'none';
      return;
    }

    const countText = stack.count > 1 ? ` x${stack.count}` : '';
    tooltipEl.innerHTML = `
      <div class="tip-name">${getItemDisplayName(def)}${countText}</div>
      <div class="tip-desc">${describeItem(def, stack)}</div>
    `;
    tooltipEl.style.display = 'block';
    tooltipEl.style.left = `${x + 16}px`;
    tooltipEl.style.top = `${y + 16}px`;
  }

  function hideTooltip() {
    if (tooltipEl) tooltipEl.style.display = 'none';
  }

  function bindTooltip(el, getStackFn) {
    el.addEventListener('mouseenter', (e) => {
      showTooltip(getStackFn(), e.clientX, e.clientY);
    });
    el.addEventListener('mousemove', (e) => {
      showTooltip(getStackFn(), e.clientX, e.clientY);
    });
    el.addEventListener('mouseleave', hideTooltip);
  }

  function refreshCraftResult() {
    craftResult = findCraftResult({
      recipes,
      craftMode,
      craftSlots,
      isEmptyStack
    });
  }

  function renderSingleSlot(el, stack, opts = {}) {
    const { active = false, keyLabel = '', isCraftResult = false } = opts;
    const itemId = stack.id;
    const def = itemId ? itemDefs[itemId] : null;
    const iconUrl = itemId ? iconProvider.getIcon(itemId) : '';

    el.className = `slot ${active ? 'active' : ''} ${isCraftResult ? 'craft-result' : ''}`;
    el.style.backgroundColor = def ? dimColor(def.color, 0.35) : '#2a2e36';

    let durabilityHtml = '';
    if (def && isDurableItemDef(def) && stack.count > 0) {
      const durability = Math.max(0, Math.min(def.maxDurability, stack.durability || def.maxDurability));
      const pct = Math.max(0, Math.min(100, (durability / def.maxDurability) * 100));
      durabilityHtml = `<div class="slot-durability"><div style="width:${pct}%"></div></div>`;
    }

    el.innerHTML = `
      <span class="slot-key">${keyLabel}</span>
      <span class="slot-icon">${iconUrl ? `<img src="${iconUrl}" alt="" draggable="false">` : ''}</span>
      <span class="slot-count">${stack.count > 1 ? stack.count : ''}</span>
      ${durabilityHtml}
    `;
  }

  function renderCursor() {
    if (!craftOpen || isEmptyStack(cursorStack)) {
      cursorEl.style.display = 'none';
      return;
    }

    const iconUrl = iconProvider.getIcon(cursorStack.id);
    cursorEl.style.display = 'flex';
    cursorEl.style.backgroundColor = dimColor(itemDefs[cursorStack.id]?.color, 0.35) || '#2a2e36';
    cursorEl.innerHTML = `
      <span class="slot-icon">${iconUrl ? `<img src="${iconUrl}" alt="" draggable="false">` : ''}</span>
      <span class="slot-count">${cursorStack.count > 1 ? cursorStack.count : ''}</span>
    `;
  }

  function renderTrashSlot() {
    if (!inventoryTrashSlotEl) return;
    inventoryTrashSlotEl.classList.toggle('has-cursor-item', !isEmptyStack(cursorStack));
  }

  function moveCursor(e) {
    cursorEl.style.left = `${e.clientX + 6}px`;
    cursorEl.style.top = `${e.clientY + 6}px`;
    if (tooltipEl && tooltipEl.style.display === 'block') {
      tooltipEl.style.left = `${e.clientX + 16}px`;
      tooltipEl.style.top = `${e.clientY + 16}px`;
    }
  }

  function addElementBinding(slotIndex, el) {
    slotElements[slotIndex].push(el);
  }

  function renderAllSlots() {
    for (let i = 0; i < TOTAL_SLOTS; i++) {
      const els = slotElements[i];
      for (const el of els) {
        const keyLabel = el.dataset.hotbarKey || '';
        renderSingleSlot(el, slots[i], {
          active: i === activeSlotIndex,
          keyLabel
        });
      }
    }
  }

  function renderEquipmentSlots() {
    for (const slotName of ARMOR_SLOT_ORDER) {
      const el = equipmentSlotEls.get(slotName);
      if (!el) continue;
      const stack = equippedArmor[slotName];
      renderSingleSlot(el, stack, {
        keyLabel: ARMOR_SLOT_SHORT[slotName] || ''
      });
    }
  }

  function renderCraftGrid() {
    const size = craftMode === 'table' ? 3 : 2;
    const grid = craftSlots[craftMode];

    craftInputEls.length = 0;
    craftingGrid.style.setProperty('--craft-size', String(size));
    craftingGrid.innerHTML = '';

    for (let i = 0; i < size * size; i++) {
      const el = document.createElement('div');
      el.className = 'slot craft-input-slot';
      el.dataset.craftIndex = String(i);
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        handleCraftInputMouseDown(i, e);
      });
      el.addEventListener('mouseenter', (e) => {
        onCraftSlotDragOver(i, e.buttons);
      });
      el.addEventListener('contextmenu', (e) => e.preventDefault());
      bindTooltip(el, () => grid[i]);
      renderSingleSlot(el, grid[i]);
      craftingGrid.appendChild(el);
      craftInputEls.push(el);
    }

    refreshCraftResult();
    renderCraftResult();
  }

  function renderCraftResult() {
    if (!craftResult) {
      renderSingleSlot(craftingResultSlot, makeEmptyStack(), { isCraftResult: true });
      return;
    }

    renderSingleSlot(craftingResultSlot, { id: craftResult.itemId, count: craftResult.count, durability: null }, { isCraftResult: true });
  }

  function handleCreativePaletteMouseDown(itemId, e) {
    if (!creativeMode) return;
    const def = itemDefs[itemId];
    if (!def) return;

    const single = e.button === 2;
    const stackCount = creativePalette.getCreativeStackCount(itemId, single);
    if (stackCount <= 0) return;
    const durability = creativePalette.getDefaultDurabilityForItem(itemId);

    if (e.shiftKey && e.button === 0) {
      insertCreativeIntoInventory(itemId, stackCount, durability);
      updateInventoryUI();
      return;
    }

    setStack(cursorStack, itemId, stackCount, durability);
    updateInventoryUI();
  }

  function renderCreativePalette() {
    creativePalette.render(craftOpen, creativeMode);
  }

  function updateModeUi() {
    if (craftMode === 'table' && !tableAccess) craftMode = 'hand';
    const isTable = craftMode === 'table';

    inventoryTitle.innerText = isTable ? t('ui.inventory_table_title') : t('ui.inventory_hand_title');
    tableHint.innerText = tableAccess ? t('ui.table_hint_available') : t('ui.table_hint_locked');

    modeToggleBtn.style.display = 'none';
    if (equipmentLabelEl) equipmentLabelEl.innerText = t('ui.equipment_label');
    if (inventoryMainLabelEl) inventoryMainLabelEl.innerText = t('ui.inventory_label');
    if (inventoryHotbarLabelEl) inventoryHotbarLabelEl.innerText = t('ui.hotbar_label');
    if (inventoryTrashLabelEl) inventoryTrashLabelEl.innerText = t('ui.trash_label');
    if (inventorySortBtnEl) inventorySortBtnEl.innerText = t('ui.sort_inventory');
    creativePalette.updateLocaleLabels(t);
    if (inventoryTipEl) inventoryTipEl.innerText = creativeMode ? t('ui.tip_creative') : t('ui.tip');
    if (inventoryTrashSlotEl) inventoryTrashSlotEl.title = t('ui.trash_hint');

    renderCraftGrid();
    renderCreativePalette();
  }

  function findSlotWithItem(itemId, from = 0, to = TOTAL_SLOTS - 1) {
    for (let i = from; i <= to; i++) {
      const s = slots[i];
      if (s.id === itemId && s.count > 0) return i;
    }
    return -1;
  }

  function findEmptySlot(preferHotbar = false) {
    if (preferHotbar) {
      for (let i = 0; i < HOTBAR_SIZE; i++) {
        if (isEmptyStack(slots[i])) return i;
      }
    }
    for (let i = HOTBAR_SIZE; i < TOTAL_SLOTS; i++) {
      if (isEmptyStack(slots[i])) return i;
    }
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      if (isEmptyStack(slots[i])) return i;
    }
    return -1;
  }

  function itemSortPriority(itemId) {
    const kind = itemDefs[itemId]?.kind || '';
    if (kind === 'block') return 0;
    if (kind === 'material') return 1;
    if (kind === 'food') return 2;
    if (kind === 'tool') return 3;
    if (kind === 'armor') return 4;
    return 5;
  }

  function sortInventoryMain() {
    const mainEntries = [];
    for (let i = HOTBAR_SIZE; i < TOTAL_SLOTS; i++) {
      const stack = slots[i];
      if (isEmptyStack(stack)) continue;
      mainEntries.push(cloneStack(stack));
      clearStack(stack);
    }
    if (mainEntries.length <= 0) {
      updateInventoryUI();
      return false;
    }

    mainEntries.sort((a, b) => {
      const p = itemSortPriority(a.id) - itemSortPriority(b.id);
      if (p !== 0) return p;
      const idCmp = String(a.id).localeCompare(String(b.id));
      if (idCmp !== 0) return idCmp;
      return Number(b.durability || 0) - Number(a.durability || 0);
    });

    const merged = [];
    for (const entry of mainEntries) {
      const def = itemDefs[entry.id];
      if (!def) continue;
      const maxStack = getStackMax(itemDefs, entry.id);
      const stackable = def.kind !== 'tool' && def.kind !== 'armor' && maxStack > 1 && !entry.durability;
      if (!stackable) {
        merged.push(cloneStack(entry));
        continue;
      }

      let remaining = entry.count;
      for (const m of merged) {
        if (remaining <= 0) break;
        if (m.id !== entry.id || m.durability) continue;
        const space = maxStack - m.count;
        if (space <= 0) continue;
        const add = Math.min(space, remaining);
        m.count += add;
        remaining -= add;
      }
      while (remaining > 0) {
        const put = Math.min(maxStack, remaining);
        merged.push({
          id: entry.id,
          count: put,
          durability: null
        });
        remaining -= put;
      }
    }

    let writeIndex = HOTBAR_SIZE;
    for (const stack of merged) {
      if (writeIndex >= TOTAL_SLOTS) break;
      setStack(slots[writeIndex], stack.id, stack.count, stack.durability ?? null);
      writeIndex += 1;
    }

    updateInventoryUI();
    return true;
  }

  function insertIntoInventory(itemId, count, durability = null) {
    if (!itemDefs[itemId] || count <= 0) return count;

    // Minecraft pickup logic:
    // 1. Stack into existing hotbar slots that already hold this item
    // 2. Stack into existing main-inventory slots that already hold this item
    // 3. Place into empty hotbar slots
    // 4. Place into empty main-inventory slots
    const def = itemDefs[itemId];
    const maxStack = getStackMax(itemDefs, itemId);
    const stackable = def.kind !== 'tool' && def.kind !== 'armor' && maxStack > 1;

    let remaining = count;

    // Phase 1+2: stack into existing slots (hotbar first, then main)
    if (stackable) {
      remaining = stackIntoExisting(itemId, remaining, maxStack, 0, HOTBAR_SIZE - 1);
      if (remaining > 0) {
        remaining = stackIntoExisting(itemId, remaining, maxStack, HOTBAR_SIZE, TOTAL_SLOTS - 1);
      }
    }

    // Phase 3+4: place into empty slots (hotbar first, then main)
    if (remaining > 0) {
      remaining = placeIntoEmpty(itemId, remaining, durability, def, maxStack, 0, HOTBAR_SIZE - 1);
    }
    if (remaining > 0) {
      remaining = placeIntoEmpty(itemId, remaining, durability, def, maxStack, HOTBAR_SIZE, TOTAL_SLOTS - 1);
    }

    return remaining;
  }

  function stackIntoExisting(itemId, count, maxStack, start, end) {
    let remaining = count;
    for (let i = start; i <= end && remaining > 0; i++) {
      const s = slots[i];
      if (s.id !== itemId) continue;
      const space = maxStack - s.count;
      if (space <= 0) continue;
      const add = Math.min(space, remaining);
      s.count += add;
      remaining -= add;
    }
    return remaining;
  }

  function placeIntoEmpty(itemId, count, durability, def, maxStack, start, end) {
    let remaining = count;
    for (let i = start; i <= end && remaining > 0; i++) {
      const s = slots[i];
      if (!isEmptyStack(s)) continue;
      const put = def.kind === 'tool' ? 1 : Math.min(maxStack, remaining);
      setStack(s, itemId, put, durability);
      remaining -= put;
    }
    return remaining;
  }

  function getInventoryInsertCapacity(itemId) {
    const def = itemDefs[itemId];
    if (!def) return 0;

    const maxStack = getStackMax(itemDefs, itemId);
    let capacity = 0;

    for (let i = 0; i < TOTAL_SLOTS; i++) {
      const s = slots[i];
      if (isEmptyStack(s)) {
        capacity += def.kind === 'tool' ? 1 : maxStack;
        continue;
      }
      if (def.kind !== 'tool' && s.id === itemId) {
        capacity += Math.max(0, maxStack - s.count);
      }
    }

    return capacity;
  }

  function insertIntoRange(itemId, count, durability, start, end, skipIndex = -1) {
    const def = itemDefs[itemId];
    if (!def || count <= 0) return count;

    let remaining = count;
    const maxStack = getStackMax(itemDefs, itemId);

    if (def.kind !== 'tool' && maxStack > 1) {
      for (let i = start; i <= end && remaining > 0; i++) {
        if (i === skipIndex) continue;
        const s = slots[i];
        if (s.id !== itemId) continue;
        const space = maxStack - s.count;
        if (space <= 0) continue;
        const add = Math.min(space, remaining);
        s.count += add;
        remaining -= add;
      }
    }

    for (let i = start; i <= end && remaining > 0; i++) {
      if (i === skipIndex) continue;
      const s = slots[i];
      if (!isEmptyStack(s)) continue;
      const put = def.kind === 'tool' ? 1 : Math.min(maxStack, remaining);
      setStack(s, itemId, put, durability);
      remaining -= put;
    }

    return remaining;
  }

  function insertCreativeIntoInventory(itemId, count, durability = null) {
    if (!itemDefs[itemId] || count <= 0) return count;
    let remaining = count;
    remaining = insertIntoRange(itemId, remaining, durability, 0, HOTBAR_SIZE - 1, -1);
    if (remaining > 0) {
      remaining = insertIntoRange(itemId, remaining, durability, HOTBAR_SIZE, TOTAL_SLOTS - 1, -1);
    }
    return remaining;
  }

  function takeFromSlot(slot, amount) {
    if (isEmptyStack(slot) || amount <= 0) return makeEmptyStack();
    const out = makeEmptyStack();
    const take = Math.min(slot.count, amount);
    out.id = slot.id;
    out.count = take;
    out.durability = slot.durability;

    slot.count -= take;
    if (slot.count <= 0) clearStack(slot);
    return out;
  }

  function mergeIntoSlot(target, source, amount = source.count) {
    if (isEmptyStack(source) || amount <= 0) return 0;
    const def = itemDefs[source.id];
    if (!def) return 0;

    if (isEmptyStack(target)) {
      const canPut = def.kind === 'tool' ? 1 : Math.min(getStackMax(itemDefs, source.id), amount);
      setStack(target, source.id, canPut, source.durability);
      source.count -= canPut;
      if (source.count <= 0) clearStack(source);
      return canPut;
    }

    if (target.id !== source.id) return 0;
    if (def.kind === 'tool') return 0;

    const maxStack = getStackMax(itemDefs, source.id);
    const space = maxStack - target.count;
    if (space <= 0) return 0;

    const put = Math.min(space, amount, source.count);
    target.count += put;
    source.count -= put;
    if (source.count <= 0) clearStack(source);
    return put;
  }

  function swapStacks(a, b) {
    const tmp = cloneStack(a);
    a.id = b.id;
    a.count = b.count;
    a.durability = b.durability;
    b.id = tmp.id;
    b.count = tmp.count;
    b.durability = tmp.durability;
  }

  function stopCraftDragPlace() {
    dragPlace.active = false;
    dragPlace.button = 0;
    dragPlace.itemId = null;
    dragPlace.visited.clear();
  }

  function stopCraftLeftDrag() {
    craftLeftDrag.active = false;
    craftLeftDrag.mode = craftMode;
    craftLeftDrag.itemId = null;
    craftLeftDrag.startIndex = -1;
    craftLeftDrag.moved = false;
    craftLeftDrag.visited.clear();
    clearStack(craftLeftDrag.baseCursor);
    craftLeftDrag.baseSlots = [];
  }

  function restoreCraftLeftDragBase() {
    if (!craftLeftDrag.active) return false;
    const dragMode = craftLeftDrag.mode;
    if (dragMode !== 'hand' && dragMode !== 'table') return false;
    const grid = craftSlots[dragMode];
    const baseSlots = Array.isArray(craftLeftDrag.baseSlots) ? craftLeftDrag.baseSlots : [];

    for (let i = 0; i < grid.length; i++) {
      const snap = baseSlots[i];
      if (!snap || isEmptyStack(snap)) clearStack(grid[i]);
      else setStack(grid[i], snap.id, snap.count, snap.durability ?? null);
    }

    if (isEmptyStack(craftLeftDrag.baseCursor)) clearStack(cursorStack);
    else {
      setStack(
        cursorStack,
        craftLeftDrag.baseCursor.id,
        craftLeftDrag.baseCursor.count,
        craftLeftDrag.baseCursor.durability ?? null
      );
    }
    return true;
  }

  function applyCraftLeftDragPreview() {
    if (!craftLeftDrag.active) return false;
    if (craftLeftDrag.mode !== craftMode) return false;
    if (!restoreCraftLeftDragBase()) return false;

    const changed = distributeCursorAcrossCraftSlots(
      Array.from(craftLeftDrag.visited),
      craftLeftDrag.itemId,
      craftLeftDrag.mode
    );
    if (!changed) return false;

    syncInventoryCounts();
    refreshCraftResult();
    renderCraftGrid();
    renderCursor();
    renderAllSlots();
    renderEquipmentSlots();
    return true;
  }

  function startCraftLeftDrag(craftIndex) {
    if (isEmptyStack(cursorStack) || craftIndex < 0) return;
    craftLeftDrag.active = true;
    craftLeftDrag.mode = craftMode;
    craftLeftDrag.itemId = cursorStack.id;
    craftLeftDrag.startIndex = craftIndex;
    craftLeftDrag.moved = false;
    craftLeftDrag.visited.clear();
    craftLeftDrag.visited.add(craftIndex);
    craftLeftDrag.baseCursor = cloneStack(cursorStack);
    craftLeftDrag.baseSlots = craftSlots[craftMode].map((slot) => cloneStack(slot));
  }

  function startCraftDragIfPossible(button = 0, initialIndex = null) {
    if (isEmptyStack(cursorStack)) {
      stopCraftDragPlace();
      return;
    }
    dragPlace.active = true;
    dragPlace.button = button;
    dragPlace.itemId = cursorStack.id;
    dragPlace.visited.clear();
    if (Number.isInteger(initialIndex) && initialIndex >= 0) {
      dragPlace.visited.add(initialIndex);
    }
  }

  function placeOneIntoCraftSlot(slot) {
    if (isEmptyStack(cursorStack)) return false;
    const def = itemDefs[cursorStack.id];
    if (!def) return false;

    if (isEmptyStack(slot)) {
      setStack(slot, cursorStack.id, 1, cursorStack.durability);
      cursorStack.count -= 1;
      if (cursorStack.count <= 0) clearStack(cursorStack);
      return true;
    }

    if (slot.id !== cursorStack.id || def.kind === 'tool') return false;

    const max = getStackMax(itemDefs, slot.id);
    if (slot.count >= max) return false;
    slot.count += 1;
    cursorStack.count -= 1;
    if (cursorStack.count <= 0) clearStack(cursorStack);
    return true;
  }

  function distributeCursorAcrossCraftSlots(craftIndices, itemId, mode) {
    if (!Array.isArray(craftIndices) || craftIndices.length <= 0) return false;
    if (isEmptyStack(cursorStack) || cursorStack.id !== itemId) return false;
    if (mode !== 'hand' && mode !== 'table') return false;

    const grid = craftSlots[mode];
    const def = itemDefs[itemId];
    if (!def) return false;

    const maxStack = getStackMax(itemDefs, itemId);
    const isSingleStack = def.kind === 'tool' || def.kind === 'armor' || maxStack <= 1;
    const candidates = [];
    for (const idx of craftIndices) {
      const slot = grid[idx];
      if (!slot) continue;
      if (isEmptyStack(slot)) {
        candidates.push({
          idx,
          slot,
          capacity: isSingleStack ? 1 : maxStack
        });
        continue;
      }
      if (slot.id !== itemId) continue;
      if (isSingleStack) continue;
      const space = maxStack - slot.count;
      if (space <= 0) continue;
      candidates.push({
        idx,
        slot,
        capacity: space
      });
    }
    if (candidates.length <= 0) return false;

    const allocations = new Map();
    let remaining = cursorStack.count;
    if (isSingleStack) {
      for (const entry of candidates) {
        if (remaining <= 0) break;
        allocations.set(entry.idx, 1);
        remaining -= 1;
      }
    } else {
      const slotCount = candidates.length;
      const evenBase = Math.floor(remaining / slotCount);
      let remainder = remaining % slotCount;
      const slack = new Map();

      for (const entry of candidates) {
        const desired = evenBase + (remainder > 0 ? 1 : 0);
        if (remainder > 0) remainder -= 1;
        const add = Math.min(entry.capacity, desired);
        if (add > 0) allocations.set(entry.idx, add);
        remaining -= add;
        slack.set(entry.idx, entry.capacity - add);
      }

      while (remaining > 0) {
        let moved = false;
        for (const entry of candidates) {
          if (remaining <= 0) break;
          const left = slack.get(entry.idx) || 0;
          if (left <= 0) continue;
          allocations.set(entry.idx, (allocations.get(entry.idx) || 0) + 1);
          slack.set(entry.idx, left - 1);
          remaining -= 1;
          moved = true;
        }
        if (!moved) break;
      }
    }

    let movedTotal = 0;
    for (const entry of candidates) {
      const add = allocations.get(entry.idx) || 0;
      if (add <= 0) continue;
      const slot = entry.slot;
      if (isEmptyStack(slot)) setStack(slot, itemId, add, cursorStack.durability);
      else slot.count += add;
      movedTotal += add;
    }
    if (movedTotal <= 0) return false;

    cursorStack.count -= movedTotal;
    if (cursorStack.count <= 0) clearStack(cursorStack);
    return true;
  }

  function finalizeCraftLeftDrag() {
    if (!craftLeftDrag.active) return false;

    const dragMode = craftLeftDrag.mode;
    const startIndex = craftLeftDrag.startIndex;
    const itemId = craftLeftDrag.itemId;
    const visited = Array.from(craftLeftDrag.visited);
    const moved = craftLeftDrag.moved;
    if (dragMode !== craftMode) {
      stopCraftLeftDrag();
      return false;
    }

    restoreCraftLeftDragBase();
    if (!moved || visited.length <= 1) {
      stopCraftLeftDrag();
      const slot = craftSlots[craftMode][startIndex];
      if (!slot) return false;
      const changed = handleGenericSlotClick(slot, false);
      if (!changed) return false;
      syncInventoryCounts();
      refreshCraftResult();
      renderCraftGrid();
      renderCursor();
      renderAllSlots();
      renderEquipmentSlots();
      return true;
    }

    const changed = distributeCursorAcrossCraftSlots(visited, itemId, dragMode);
    stopCraftLeftDrag();
    if (!changed) return false;
    syncInventoryCounts();
    refreshCraftResult();
    renderCraftGrid();
    renderCursor();
    renderAllSlots();
    renderEquipmentSlots();
    return true;
  }

  function onCraftSlotDragOver(craftIndex, buttons = 0) {
    if (craftLeftDrag.active) {
      if (craftLeftDrag.mode !== craftMode) {
        restoreCraftLeftDragBase();
        stopCraftLeftDrag();
        syncInventoryCounts();
        refreshCraftResult();
        renderCraftGrid();
        renderCursor();
        renderAllSlots();
        renderEquipmentSlots();
        return;
      }
      if (!craftLeftDrag.visited.has(craftIndex)) {
        craftLeftDrag.visited.add(craftIndex);
        if (craftIndex !== craftLeftDrag.startIndex) craftLeftDrag.moved = true;
        if (craftLeftDrag.moved) applyCraftLeftDragPreview();
      }
      return;
    }

    if (!dragPlace.active) return;
    if ((dragPlace.button === 0 && (buttons & 1) === 0) || (dragPlace.button === 2 && (buttons & 2) === 0)) {
      stopCraftDragPlace();
      return;
    }

    if (dragPlace.visited.has(craftIndex)) return;
    if (isEmptyStack(cursorStack) || cursorStack.id !== dragPlace.itemId) {
      stopCraftDragPlace();
      return;
    }

    const slot = craftSlots[craftMode][craftIndex];
    const placed = placeOneIntoCraftSlot(slot);
    dragPlace.visited.add(craftIndex);
    if (!placed) return;

    syncInventoryCounts();
    refreshCraftResult();
    renderCraftGrid();
    renderCursor();
    renderAllSlots();
    renderEquipmentSlots();
  }

  function postSlotMutation() {
    syncInventoryCounts();
    refreshCraftResult();
    renderCraftResult();
    renderAllSlots();
    renderEquipmentSlots();
    renderCursor();
  }

  function quickMoveInventorySlot(slotIndex) {
    const source = slots[slotIndex];
    if (isEmptyStack(source)) return false;

    if (typeof onQuickMoveInventorySlot === 'function') {
      const movedExternal = Math.max(0, Math.floor(Number(onQuickMoveInventorySlot({
        slotIndex,
        itemId: source.id,
        count: source.count,
        durability: source.durability ?? null
      })) || 0));
      if (movedExternal > 0) {
        source.count -= movedExternal;
        if (source.count <= 0) clearStack(source);
        return true;
      }
    }

    const sourceDef = itemDefs[source.id];
    if (sourceDef && sourceDef.kind === 'armor' && sourceDef.armorSlot) {
      const armorSlot = equippedArmor[sourceDef.armorSlot];
      if (armorSlot && isEmptyStack(armorSlot)) {
        setStack(armorSlot, source.id, 1, source.durability);
        source.count -= 1;
        if (source.count <= 0) clearStack(source);
        return true;
      }
      if (armorSlot && !isEmptyStack(armorSlot)) {
        const oldId = armorSlot.id;
        const oldDurability = armorSlot.durability;
        setStack(armorSlot, source.id, 1, source.durability);
        setStack(source, oldId, 1, oldDurability);
        return true;
      }
    }

    const movingId = source.id;
    const movingDurability = source.durability;
    const movingCount = source.count;
    clearStack(source);

    let remaining;
    if (slotIndex < HOTBAR_SIZE) {
      remaining = insertIntoRange(movingId, movingCount, movingDurability, HOTBAR_SIZE, TOTAL_SLOTS - 1, slotIndex);
      if (remaining > 0) {
        remaining = insertIntoRange(movingId, remaining, movingDurability, 0, HOTBAR_SIZE - 1, slotIndex);
      }
    } else {
      remaining = insertIntoRange(movingId, movingCount, movingDurability, 0, HOTBAR_SIZE - 1, slotIndex);
      if (remaining > 0) {
        remaining = insertIntoRange(movingId, remaining, movingDurability, HOTBAR_SIZE, TOTAL_SLOTS - 1, slotIndex);
      }
    }
    if (remaining > 0) {
      setStack(source, movingId, remaining, movingDurability);
    }

    return remaining < movingCount;
  }

  function collectSimilarToCursor(itemId) {
    if (!itemDefs[itemId]) return false;

    const def = itemDefs[itemId];
    const max = getStackMax(itemDefs, itemId);
    if (!def || def.kind === 'tool' || def.kind === 'armor' || max <= 1) return false;

    if (isEmptyStack(cursorStack)) {
      cursorStack.id = itemId;
      cursorStack.count = 0;
      cursorStack.durability = null;
    }

    if (cursorStack.id !== itemId) return false;
    if (cursorStack.count >= max) return false;

    const candidates = [];
    let order = 0;
    const pushCandidate = (stack) => {
      if (isEmptyStack(stack) || stack.id !== itemId) return;
      candidates.push({
        stack,
        count: stack.count,
        partialRank: stack.count < max ? 0 : 1,
        order: order++
      });
    };

    for (const s of slots) pushCandidate(s);
    for (const s of craftSlots[craftMode]) pushCandidate(s);

    // Prefer consuming smaller partial stacks first, and touch full stacks last.
    candidates.sort((a, b) => {
      if (a.partialRank !== b.partialRank) return a.partialRank - b.partialRank;
      if (a.partialRank === 0 && a.count !== b.count) return a.count - b.count;
      return a.order - b.order;
    });

    let movedAny = false;
    for (const entry of candidates) {
      if (cursorStack.count >= max) break;
      const stack = entry.stack;
      if (isEmptyStack(stack) || stack.id !== itemId) continue;
      const space = max - cursorStack.count;
      if (space <= 0) break;
      const move = Math.min(space, stack.count);
      if (move <= 0) continue;
      cursorStack.count += move;
      stack.count -= move;
      if (stack.count <= 0) clearStack(stack);
      movedAny = true;
    }

    const externalSpace = max - cursorStack.count;
    if (externalSpace > 0 && typeof onCollectSimilarExternal === 'function') {
      const movedExternal = Math.max(0, Math.floor(Number(onCollectSimilarExternal({
        itemId,
        maxTake: externalSpace
      })) || 0));
      if (movedExternal > 0) {
        cursorStack.count = Math.min(max, cursorStack.count + movedExternal);
        movedAny = true;
      }
    }

    return movedAny;
  }

  function getTrackedDoubleClickItem(fallbackItemId = null) {
    return cursorStack.id || fallbackItemId || null;
  }

  function canDoubleCollectItem(itemId) {
    if (!itemId) return false;
    const def = itemDefs[itemId];
    if (!def) return false;
    if (def.kind === 'tool' || def.kind === 'armor') return false;
    return getStackMax(itemDefs, itemId) > 1;
  }

  function recordClick(itemId, source = 'ui') {
    clickTracker.itemId = itemId;
    clickTracker.time = performance.now();
    clickTracker.source = source;
  }

  function isDoubleClick(itemId, source = 'ui') {
    const now = performance.now();
    if (!itemId) return false;
    const ok = (
      clickTracker.itemId === itemId &&
      clickTracker.source === source &&
      (now - clickTracker.time) < 300
    );
    if (ok) {
      clickTracker.itemId = null;
      clickTracker.time = 0;
      clickTracker.source = null;
      return true;
    }
    return false;
  }

  function resetDoubleClickTracker() {
    clickTracker.itemId = null;
    clickTracker.time = 0;
    clickTracker.source = null;
  }

  function handleGenericSlotClick(slot, isRightClick) {
    const slotEmpty = isEmptyStack(slot);
    const cursorEmpty = isEmptyStack(cursorStack);

    if (!isRightClick) {
      if (cursorEmpty && !slotEmpty) {
        const moved = takeFromSlot(slot, slot.count);
        setStack(cursorStack, moved.id, moved.count, moved.durability);
        return true;
      }

      if (!cursorEmpty && slotEmpty) {
        setStack(slot, cursorStack.id, cursorStack.count, cursorStack.durability);
        clearStack(cursorStack);
        return true;
      }

      if (!cursorEmpty && !slotEmpty) {
        if (slot.id === cursorStack.id && itemDefs[slot.id]?.kind !== 'tool') {
          const moved = mergeIntoSlot(slot, cursorStack, cursorStack.count);
          return moved > 0;
        }
        swapStacks(slot, cursorStack);
        return true;
      }

      return false;
    }

    if (cursorEmpty && !slotEmpty) {
      const takeCount = itemDefs[slot.id]?.kind === 'tool' ? 1 : Math.ceil(slot.count / 2);
      const moved = takeFromSlot(slot, takeCount);
      setStack(cursorStack, moved.id, moved.count, moved.durability);
      return true;
    }

    if (!cursorEmpty) {
      const def = itemDefs[cursorStack.id];
      if (!def) return false;
      if (slotEmpty) {
        const moved = takeFromSlot(cursorStack, 1);
        setStack(slot, moved.id, moved.count, moved.durability);
        return true;
      }
      if (slot.id === cursorStack.id && def.kind !== 'tool') {
        const moved = mergeIntoSlot(slot, cursorStack, 1);
        return moved > 0;
      }
    }

    return false;
  }

  function moveExternalSlotToInventory(slot, desiredCount = Number.POSITIVE_INFINITY) {
    if (isEmptyStack(slot)) return 0;
    const take = Math.max(0, Math.floor(Number(desiredCount)));
    const requested = Number.isFinite(take) ? Math.min(slot.count, take) : slot.count;
    if (requested <= 0) return 0;

    const remaining = insertIntoInventory(slot.id, requested, slot.durability ?? null);
    const moved = requested - remaining;
    if (moved <= 0) return 0;

    slot.count -= moved;
    if (slot.count <= 0) clearStack(slot);
    postSlotMutation();
    return moved;
  }

  function moveExternalSlotToCursor(slot, desiredCount = Number.POSITIVE_INFINITY) {
    if (isEmptyStack(slot)) return 0;
    const def = itemDefs[slot.id];
    if (!def) return 0;

    const take = Math.max(0, Math.floor(Number(desiredCount)));
    const requested = Number.isFinite(take) ? Math.min(slot.count, take) : slot.count;
    if (requested <= 0) return 0;

    if (isEmptyStack(cursorStack)) {
      const move = def.kind === 'tool' || def.kind === 'armor'
        ? 1
        : Math.min(requested, getStackMax(itemDefs, slot.id));
      if (move <= 0) return 0;
      setStack(cursorStack, slot.id, move, slot.durability ?? null);
      slot.count -= move;
      if (slot.count <= 0) clearStack(slot);
      postSlotMutation();
      return move;
    }

    if (cursorStack.id !== slot.id || def.kind === 'tool' || def.kind === 'armor') return 0;

    const maxStack = getStackMax(itemDefs, slot.id);
    const space = maxStack - cursorStack.count;
    if (space <= 0) return 0;

    const move = Math.min(requested, space);
    if (move <= 0) return 0;
    cursorStack.count += move;
    slot.count -= move;
    if (slot.count <= 0) clearStack(slot);
    postSlotMutation();
    return move;
  }

  function moveCursorToExternalSlot(slot, desiredCount = Number.POSITIVE_INFINITY, { acceptsItem = null } = {}) {
    if (isEmptyStack(cursorStack)) return 0;
    const def = itemDefs[cursorStack.id];
    if (!def) return 0;
    if (typeof acceptsItem === 'function' && !acceptsItem(cursorStack.id, slot, cursorStack)) return 0;

    const putLimit = Math.max(0, Math.floor(Number(desiredCount)));
    const requested = Number.isFinite(putLimit) ? Math.min(cursorStack.count, putLimit) : cursorStack.count;
    if (requested <= 0) return 0;

    if (isEmptyStack(slot)) {
      const move = def.kind === 'tool' || def.kind === 'armor'
        ? 1
        : Math.min(requested, getStackMax(itemDefs, cursorStack.id));
      if (move <= 0) return 0;
      setStack(slot, cursorStack.id, move, cursorStack.durability ?? null);
      cursorStack.count -= move;
      if (cursorStack.count <= 0) clearStack(cursorStack);
      postSlotMutation();
      return move;
    }

    if (slot.id !== cursorStack.id || def.kind === 'tool' || def.kind === 'armor') return 0;
    const maxStack = getStackMax(itemDefs, cursorStack.id);
    const space = maxStack - slot.count;
    if (space <= 0) return 0;

    const move = Math.min(requested, space);
    if (move <= 0) return 0;
    slot.count += move;
    cursorStack.count -= move;
    if (cursorStack.count <= 0) clearStack(cursorStack);
    postSlotMutation();
    return move;
  }

  function placeOneIntoExternalSlot(slot, { acceptsItem = null } = {}) {
    return moveCursorToExternalSlot(slot, 1, { acceptsItem });
  }

  function swapCursorWithExternalSlot(slot, { acceptsItem = null } = {}) {
    if (isEmptyStack(cursorStack)) return false;
    if (typeof acceptsItem === 'function' && !acceptsItem(cursorStack.id, slot, cursorStack)) return false;

    const nextSlot = cloneStack(cursorStack);
    const nextCursor = cloneStack(slot);
    setStack(slot, nextSlot.id, nextSlot.count, nextSlot.durability ?? null);
    if (isEmptyStack(nextCursor)) clearStack(cursorStack);
    else setStack(cursorStack, nextCursor.id, nextCursor.count, nextCursor.durability ?? null);
    postSlotMutation();
    return true;
  }

  function handleExternalSlotMouseDown(slot, e, { acceptsItem = null, onChange = null } = {}) {
    if (!slot || !e || (e.button !== 0 && e.button !== 2)) return false;
    const isRightClick = e.button === 2;
    let changed = false;

    if (e.shiftKey && !isRightClick) {
      changed = moveExternalSlotToInventory(slot, slot.count) > 0;
    } else if (!isRightClick) {
      if (isEmptyStack(cursorStack)) {
        changed = moveExternalSlotToCursor(slot, slot.count) > 0;
      } else if (isEmptyStack(slot)) {
        changed = moveCursorToExternalSlot(slot, cursorStack.count, { acceptsItem }) > 0;
      } else if (slot.id === cursorStack.id && itemDefs[slot.id]?.kind !== 'tool') {
        changed = moveCursorToExternalSlot(slot, cursorStack.count, { acceptsItem }) > 0;
      } else {
        changed = swapCursorWithExternalSlot(slot, { acceptsItem });
      }
    } else if (isEmptyStack(cursorStack)) {
      const takeCount = itemDefs[slot.id]?.kind === 'tool' ? 1 : Math.ceil(slot.count / 2);
      changed = moveExternalSlotToCursor(slot, takeCount) > 0;
    } else {
      changed = moveCursorToExternalSlot(slot, 1, { acceptsItem }) > 0;
    }

    if (changed) {
      resetDoubleClickTracker();
      if (typeof onChange === 'function') onChange();
    }
    return changed;
  }

  function handleExternalOutputSlotMouseDown(slot, e, { onTake = null, onChange = null } = {}) {
    if (!slot || !e || (e.button !== 0 && e.button !== 2)) return false;
    if (isEmptyStack(slot)) return false;

    const itemId = slot.id;
    let moved = 0;
    if (e.shiftKey && e.button === 0) {
      moved = moveExternalSlotToInventory(slot, slot.count);
    } else if (e.button === 0) {
      moved = moveExternalSlotToCursor(slot, slot.count);
    } else if (e.button === 2) {
      moved = moveExternalSlotToCursor(slot, 1);
    }

    if (moved > 0) {
      resetDoubleClickTracker();
      if (typeof onTake === 'function') {
        onTake({
          itemId,
          count: moved
        });
      }
      if (typeof onChange === 'function') onChange();
      return true;
    }
    return false;
  }

  function getCursorItemId() {
    return isEmptyStack(cursorStack) ? null : cursorStack.id;
  }

  function handleInventorySlotMouseDown(slotIndex, e) {
    const isRightClick = e.button === 2;
    const preItemId = slots[slotIndex].id;
    const trackedItemId = getTrackedDoubleClickItem(preItemId);

    if (e.shiftKey && !isRightClick) {
      if (quickMoveInventorySlot(slotIndex)) {
        postSlotMutation();
      }
      resetDoubleClickTracker();
      return;
    }

    if (!isRightClick && trackedItemId && isDoubleClick(trackedItemId, 'ui') && canDoubleCollectItem(trackedItemId)) {
      if (collectSimilarToCursor(trackedItemId)) {
        syncInventoryCounts();
        refreshCraftResult();
        renderCraftGrid();
        renderCursor();
        renderAllSlots();
        renderEquipmentSlots();
      }
      return;
    }

    const changed = handleGenericSlotClick(slots[slotIndex], isRightClick);
    if (changed) {
      postSlotMutation();
      if (!isRightClick) startCraftDragIfPossible(e.button);
    }
    if (!isRightClick) recordClick(getTrackedDoubleClickItem(preItemId), 'ui');
  }

  function tryUnequipArmorSlot(slotName) {
    const slot = equippedArmor[slotName];
    if (!slot || isEmptyStack(slot)) return false;
    const remain = insertIntoInventory(slot.id, slot.count, slot.durability);
    if (remain > 0) return false;
    clearStack(slot);
    return true;
  }

  function handleArmorSlotMouseDown(slotName, e) {
    const isRightClick = e.button === 2;
    const slot = equippedArmor[slotName];
    if (!slot) return;

    if (e.shiftKey && !isRightClick) {
      if (tryUnequipArmorSlot(slotName)) postSlotMutation();
      return;
    }

    const cursorEmpty = isEmptyStack(cursorStack);
    const slotEmpty = isEmptyStack(slot);

    if (cursorEmpty && !slotEmpty) {
      const moved = takeFromSlot(slot, 1);
      setStack(cursorStack, moved.id, moved.count, moved.durability);
      postSlotMutation();
      return;
    }

    if (cursorEmpty) return;

    const canEquip = canEquipToArmorSlot(cursorStack.id, slotName);
    if (!canEquip) return;

    if (slotEmpty) {
      const moved = takeFromSlot(cursorStack, 1);
      setStack(slot, moved.id, moved.count, moved.durability);
      postSlotMutation();
      return;
    }

    if (cursorStack.count !== 1) return;
    swapStacks(slot, cursorStack);
    postSlotMutation();
  }

  function handleCraftInputMouseDown(craftIndex, e) {
    const grid = craftSlots[craftMode];
    const slot = grid[craftIndex];
    const isRightClick = e.button === 2;
    const preItemId = slot.id;
    const trackedItemId = getTrackedDoubleClickItem(preItemId);

    if (!isRightClick && !e.shiftKey && trackedItemId && isDoubleClick(trackedItemId, 'ui') && canDoubleCollectItem(trackedItemId)) {
      if (collectSimilarToCursor(trackedItemId)) {
        syncInventoryCounts();
        refreshCraftResult();
        renderCraftGrid();
        renderCursor();
        renderAllSlots();
        renderEquipmentSlots();
      }
      return;
    }

    if (!isRightClick && !e.shiftKey && !isEmptyStack(cursorStack)) {
      startCraftLeftDrag(craftIndex);
      return;
    }

    if (e.shiftKey && !isRightClick && !isEmptyStack(slot)) {
      const remaining = insertIntoInventory(slot.id, slot.count, slot.durability);
      slot.count = remaining;
      if (slot.count <= 0) clearStack(slot);
      syncInventoryCounts();
      refreshCraftResult();
      renderCraftGrid();
      renderCursor();
      renderAllSlots();
      renderEquipmentSlots();
      resetDoubleClickTracker();
      return;
    }

    const changed = handleGenericSlotClick(slot, isRightClick);
    if (!changed) {
      if (!isRightClick) recordClick(getTrackedDoubleClickItem(preItemId), 'ui');
      return;
    }

    startCraftDragIfPossible(e.button, craftIndex);

    syncInventoryCounts();
    refreshCraftResult();
    renderCraftGrid();
    renderCursor();
    renderAllSlots();
    renderEquipmentSlots();
    if (!isRightClick) recordClick(getTrackedDoubleClickItem(preItemId), 'ui');
  }

  function consumeCraftIngredients() {
    if (!craftResult) return false;
    const grid = craftSlots[craftMode];
    for (const idx of craftResult.consume) {
      const slot = grid[idx];
      if (isEmptyStack(slot)) return false;
      slot.count -= 1;
      if (slot.count <= 0) clearStack(slot);
    }
    return true;
  }

  function handleCraftResultClick(isRightClick, shiftHeld = false) {
    if (!craftResult) return;

    if (shiftHeld && !isRightClick) {
      let crafted = 0;
      let craftedItemId = null;
      let loopGuard = 512;

      while (craftResult && loopGuard > 0) {
        loopGuard -= 1;

        const outId = craftResult.itemId;
        const outCount = craftResult.count;
        const recipeId = craftResult.recipeId;
        if (!itemDefs[outId]) break;

        if (getInventoryInsertCapacity(outId) < outCount) break;
        if (!consumeCraftIngredients()) break;

        const remain = insertIntoInventory(outId, outCount, null);
        if (remain > 0) break;

        crafted += 1;
        craftedItemId = outId;
        if (onCraft) onCraft(recipeId, outId, outCount);

        refreshCraftResult();
      }

      if (crafted <= 0) return;
      if (craftedItemId) assignItemToHotbar(craftedItemId);

      syncInventoryCounts();
      renderCraftGrid();
      renderCursor();
      renderAllSlots();
      renderEquipmentSlots();
      return;
    }

    const outId = craftResult.itemId;
    const outCount = craftResult.count;
    const outDef = itemDefs[outId];
    if (!outDef) return;

    if (isEmptyStack(cursorStack)) {
      setStack(cursorStack, outId, outCount, null);
    } else if (cursorStack.id === outId && outDef.kind !== 'tool') {
      const max = getStackMax(itemDefs, outId);
      if (cursorStack.count + outCount > max) return;
      cursorStack.count += outCount;
    } else {
      return;
    }

    if (!consumeCraftIngredients()) return;
    if (onCraft) onCraft(craftResult.recipeId, outId, outCount);
    if (!isRightClick) assignItemToHotbar(outId);

    syncInventoryCounts();
    refreshCraftResult();
    renderCraftGrid();
    renderCursor();
    renderAllSlots();
    renderEquipmentSlots();
  }

  function assignItemToHotbar(itemId) {
    if (!itemId) return false;
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      if (slots[i].id === itemId && slots[i].count > 0) return false;
    }

    const source = findSlotWithItem(itemId, HOTBAR_SIZE, TOTAL_SLOTS - 1);
    if (source < 0) return false;

    const emptyHotbar = findEmptySlot(true);
    if (emptyHotbar < 0 || emptyHotbar >= HOTBAR_SIZE) return false;
    swapStacks(slots[source], slots[emptyHotbar]);
    return true;
  }

  function flushCraftGrid(mode) {
    const grid = craftSlots[mode];
    for (const cell of grid) {
      if (isEmptyStack(cell)) continue;
      const remain = insertIntoInventory(cell.id, cell.count, cell.durability);
      if (remain <= 0) {
        clearStack(cell);
      } else {
        cell.count = remain;
      }
    }
  }

  function updateInventoryUI() {
    syncInventoryCounts();
    renderAllSlots();
    renderEquipmentSlots();
    renderCursor();
    renderTrashSlot();
    if (craftOpen) renderCraftResult();
    renderCreativePalette();
  }

  function normalizeSavedStack(raw) {
    const itemId = String(raw?.id || '');
    const def = itemDefs[itemId];
    if (!def) return null;

    let count = Math.floor(Number(raw?.count));
    if (!Number.isFinite(count) || count <= 0) return null;
    if (def.kind === 'tool' || def.kind === 'armor') {
      count = 1;
    } else {
      count = Math.min(getStackMax(itemDefs, itemId), count);
    }

    let durability = null;
    if (isDurableItemDef(def)) {
      const rawDurability = Math.floor(Number(raw?.durability));
      durability = Number.isFinite(rawDurability)
        ? Math.max(1, Math.min(def.maxDurability, rawDurability))
        : def.maxDurability;
    }

    return { id: itemId, count, durability };
  }

  function serializeStack(slot) {
    if (isEmptyStack(slot)) return null;
    return {
      id: slot.id,
      count: slot.count,
      durability: slot.durability ?? null
    };
  }

  function exportState() {
    const armor = {};
    for (const slotName of ARMOR_SLOT_ORDER) {
      armor[slotName] = serializeStack(equippedArmor[slotName]);
    }

    return {
      activeSlotIndex,
      slots: slots.map((slot) => serializeStack(slot)),
      armor
    };
  }

  function importState(snapshot = null) {
    for (const slot of slots) clearStack(slot);
    for (const slotName of ARMOR_SLOT_ORDER) clearStack(equippedArmor[slotName]);
    for (const slot of craftSlots.hand) clearStack(slot);
    for (const slot of craftSlots.table) clearStack(slot);
    clearStack(cursorStack);

    const savedSlots = Array.isArray(snapshot?.slots) ? snapshot.slots : [];
    for (let i = 0; i < Math.min(savedSlots.length, TOTAL_SLOTS); i++) {
      const normalized = normalizeSavedStack(savedSlots[i]);
      if (!normalized) continue;
      setStack(slots[i], normalized.id, normalized.count, normalized.durability);
    }

    const savedArmor = snapshot && typeof snapshot.armor === 'object' ? snapshot.armor : null;
    if (savedArmor) {
      for (const slotName of ARMOR_SLOT_ORDER) {
        const normalized = normalizeSavedStack(savedArmor[slotName]);
        if (!normalized || !canEquipToArmorSlot(normalized.id, slotName)) continue;
        setStack(equippedArmor[slotName], normalized.id, 1, normalized.durability);
      }
    }

    const nextActiveSlot = Math.floor(Number(snapshot?.activeSlotIndex));
    activeSlotIndex = Number.isFinite(nextActiveSlot)
      ? Math.max(0, Math.min(HOTBAR_SIZE - 1, nextActiveSlot))
      : 0;

    stopCraftDragPlace();
    stopCraftLeftDrag();
    refreshCraftResult();
    hideTooltip();
    updateModeUi();
    updateInventoryUI();
    return true;
  }

  function getActiveItemId() {
    const s = slots[activeSlotIndex];
    return s && s.count > 0 ? s.id : null;
  }

  function getActiveItemCount() {
    const s = slots[activeSlotIndex];
    return s ? s.count : 0;
  }

  function getActiveItemDef() {
    const id = getActiveItemId();
    return id ? itemDefs[id] : null;
  }

  function setActiveSlot(indexNumber) {
    const idx = indexNumber - 1;
    if (idx < 0 || idx >= HOTBAR_SIZE) return;
    activeSlotIndex = idx;
    updateInventoryUI();
  }

  function addItem(itemId, count = 1) {
    if (!itemDefs[itemId] || count <= 0) return false;
    const remain = insertIntoInventory(itemId, count, null);
    updateInventoryUI();
    return remain <= 0;
  }

  function removeItem(itemId, count = 1) {
    if (count <= 0) return false;
    if ((inventory[itemId] || 0) < count) return false;
    let remaining = count;

    for (let i = 0; i < TOTAL_SLOTS && remaining > 0; i++) {
      const s = slots[i];
      if (s.id !== itemId || s.count <= 0) continue;
      const take = Math.min(s.count, remaining);
      s.count -= take;
      remaining -= take;
      if (s.count <= 0) clearStack(s);
    }

    updateInventoryUI();
    return true;
  }

  function dropAllItems(onDrop = null) {
    let droppedCount = 0;

    const emitStack = (stack, source) => {
      if (isEmptyStack(stack)) return;
      if (typeof onDrop === 'function') {
        onDrop({
          itemId: stack.id,
          count: stack.count,
          durability: stack.durability ?? null,
          source
        });
      }
      droppedCount += stack.count;
      clearStack(stack);
    };

    for (const stack of slots) emitStack(stack, 'inventory');
    for (const slotName of ARMOR_SLOT_ORDER) {
      emitStack(equippedArmor[slotName], `armor:${slotName}`);
    }
    emitStack(cursorStack, 'cursor');

    for (const stack of craftSlots.hand) emitStack(stack, 'craft:hand');
    for (const stack of craftSlots.table) emitStack(stack, 'craft:table');

    stopCraftDragPlace();
    refreshCraftResult();
    updateInventoryUI();
    return droppedCount;
  }

  function consumeActiveItem(count = 1) {
    const slot = slots[activeSlotIndex];
    if (isEmptyStack(slot) || slot.count < count) return false;
    slot.count -= count;
    if (slot.count <= 0) clearStack(slot);
    updateInventoryUI();
    return true;
  }

  function dropActiveItem(count = 1) {
    const slot = slots[activeSlotIndex];
    if (isEmptyStack(slot)) return null;

    const def = itemDefs[slot.id];
    const desired = Math.max(1, Math.floor(Number(count) || 1));
    const dropCount = (def?.kind === 'tool' || def?.kind === 'armor')
      ? 1
      : Math.min(slot.count, desired);
    if (dropCount <= 0) return null;

    const itemId = slot.id;
    const durability = slot.durability ?? null;
    slot.count -= dropCount;
    if (slot.count <= 0) clearStack(slot);
    updateInventoryUI();
    return {
      itemId,
      count: dropCount,
      durability
    };
  }

  function hasItem(itemId, count = 1) {
    return (inventory[itemId] || 0) >= count;
  }

  function damageActiveTool(amount = 1) {
    const slot = slots[activeSlotIndex];
    if (isEmptyStack(slot)) return;
    const def = itemDefs[slot.id];
    if (!def || def.kind !== 'tool') return;
    if (!isDurableItemDef(def)) return;
    ensureToolDurability(slot);
    slot.durability -= amount;
    if (slot.durability <= 0) {
      slot.count -= 1;
      if (slot.count <= 0) {
        clearStack(slot);
      } else {
        slot.durability = def.maxDurability;
      }
    }
    updateInventoryUI();
  }

  function getArmorSlotStack(slotName) {
    return equippedArmor[slotName] || null;
  }

  function getArmorPoints() {
    let total = 0;
    for (const slot of Object.values(equippedArmor)) {
      if (isEmptyStack(slot)) continue;
      const def = itemDefs[slot.id];
      if (!def || def.kind !== 'armor') continue;
      total += Number(def.armor || 0);
    }
    return total;
  }

  function getArmorReduction() {
    return Math.min(0.8, getArmorPoints() / 25);
  }

  function damageEquippedArmor(amount = 1) {
    const dmg = Math.max(0, Math.floor(amount));
    if (dmg <= 0) return;

    for (const slot of Object.values(equippedArmor)) {
      if (isEmptyStack(slot)) continue;
      const def = itemDefs[slot.id];
      if (!isDurableItemDef(def)) continue;

      ensureToolDurability(slot);
      slot.durability -= dmg;
      if (slot.durability <= 0) {
        clearStack(slot);
      }
    }
    updateInventoryUI();
  }

  function tryEquipActiveArmor() {
    const active = slots[activeSlotIndex];
    if (isEmptyStack(active) || active.count <= 0) return false;

    const def = itemDefs[active.id];
    if (!def || def.kind !== 'armor' || !def.armorSlot) return false;

    const target = getArmorSlotStack(def.armorSlot);
    if (!target) return false;

    ensureToolDurability(active);
    if (isEmptyStack(target)) {
      setStack(target, active.id, 1, active.durability);
      active.count -= 1;
      if (active.count <= 0) clearStack(active);
      updateInventoryUI();
      return true;
    }

    const oldId = target.id;
    const oldDurability = target.durability;
    setStack(target, active.id, 1, active.durability);
    setStack(active, oldId, 1, oldDurability);
    updateInventoryUI();
    return true;
  }

  function setCraftMode(mode) {
    if (mode !== 'hand' && mode !== 'table') return false;
    if (mode === 'table' && !tableAccess) return false;
    craftMode = mode;
    updateModeUi();
    return true;
  }

  function setTableAccess(v) {
    tableAccess = !!v;
    if (!tableAccess && craftMode === 'table') craftMode = 'hand';
    updateModeUi();
  }

  function setCreativeMode(v) {
    creativeMode = !!v;
    updateModeUi();
    updateInventoryUI();
  }

  function setCraftOpen(v, opts = {}) {
    craftOpen = !!v;
    if (typeof opts.tableAccess === 'boolean') tableAccess = opts.tableAccess;
    if (opts.mode === 'table' && tableAccess) craftMode = 'table';
    else if (opts.mode === 'hand') craftMode = 'hand';

    if (!craftOpen) {
      flushCraftGrid('hand');
      flushCraftGrid('table');
      if (!isEmptyStack(cursorStack)) {
        const remain = insertIntoInventory(cursorStack.id, cursorStack.count, cursorStack.durability);
        if (remain <= 0) clearStack(cursorStack);
        else cursorStack.count = remain;
      }
      hideTooltip();
      stopCraftDragPlace();
      stopCraftLeftDrag();
      resetDoubleClickTracker();
    }

    inventoryOverlay.style.display = craftOpen ? 'flex' : 'none';
    document.body.classList.toggle('inventory-open', craftOpen);
    updateModeUi();
    updateInventoryUI();
  }

  function toggleCraftOpen(opts = {}) {
    setCraftOpen(!craftOpen, opts);
  }

  function isCraftOpen() {
    return craftOpen;
  }

  function getCraftMode() {
    return craftMode;
  }

  function hasCursorItem() {
    return !isEmptyStack(cursorStack);
  }

  function setupSlotDom() {
    hotbarUI.innerHTML = '';
    if (equipmentGrid) equipmentGrid.innerHTML = '';
    inventoryMainGrid.innerHTML = '';
    inventoryHotbarGrid.innerHTML = '';

    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const worldSlot = document.createElement('div');
      worldSlot.className = 'slot';
      worldSlot.dataset.hotbarKey = String(i + 1);
      worldSlot.addEventListener('mousedown', (e) => {
        e.preventDefault();
        if (craftOpen) {
          handleInventorySlotMouseDown(i, e);
          return;
        }
        if (e.button === 0) {
          activeSlotIndex = i;
          updateInventoryUI();
        }
      });
      worldSlot.addEventListener('contextmenu', (e) => e.preventDefault());
      bindTooltip(worldSlot, () => slots[i]);
      hotbarUI.appendChild(worldSlot);
      addElementBinding(i, worldSlot);
    }

    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < HOTBAR_SIZE; col++) {
        const idx = HOTBAR_SIZE + row * HOTBAR_SIZE + col;
        const el = document.createElement('div');
        el.className = 'slot';
        el.addEventListener('mousedown', (e) => {
          e.preventDefault();
          handleInventorySlotMouseDown(idx, e);
        });
        el.addEventListener('contextmenu', (e) => e.preventDefault());
        bindTooltip(el, () => slots[idx]);
        inventoryMainGrid.appendChild(el);
        addElementBinding(idx, el);
      }
    }

    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const el = document.createElement('div');
      el.className = 'slot';
      el.dataset.hotbarKey = String(i + 1);
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        handleInventorySlotMouseDown(i, e);
      });
      el.addEventListener('contextmenu', (e) => e.preventDefault());
      bindTooltip(el, () => slots[i]);
      inventoryHotbarGrid.appendChild(el);
      addElementBinding(i, el);
    }

    if (equipmentGrid) {
      for (const slotName of ARMOR_SLOT_ORDER) {
        const el = document.createElement('div');
        el.className = 'slot equipment-slot';
        el.dataset.equipmentSlot = slotName;
        el.addEventListener('mousedown', (e) => {
          e.preventDefault();
          handleArmorSlotMouseDown(slotName, e);
        });
        el.addEventListener('contextmenu', (e) => e.preventDefault());
        bindTooltip(el, () => equippedArmor[slotName]);
        equipmentGrid.appendChild(el);
        equipmentSlotEls.set(slotName, el);
      }
    }
  }

  modeToggleBtn.addEventListener('click', () => {
    // Manual mode switching is disabled: mode is determined by table access context.
  });

  craftingResultSlot.addEventListener('mousedown', (e) => {
    e.preventDefault();
    handleCraftResultClick(e.button === 2, e.shiftKey);
  });
  craftingResultSlot.addEventListener('contextmenu', (e) => e.preventDefault());
  bindTooltip(craftingResultSlot, () => (craftResult ? { id: craftResult.itemId, count: craftResult.count, durability: null } : makeEmptyStack()));

  function tryDropCursorOutside(e) {
    if (!craftOpen || isEmptyStack(cursorStack)) return false;
    if (!e || (e.button !== 0 && e.button !== 2)) return false;
    if (typeof onDropCursorOutside !== 'function') return false;
    if (e.target instanceof Element && e.target.closest('[data-inventory-panel]')) return false;

    const desiredCount = e.button === 2 ? 1 : cursorStack.count;
    const moved = Math.max(0, Math.floor(Number(onDropCursorOutside({
      itemId: cursorStack.id,
      count: desiredCount,
      durability: cursorStack.durability ?? null,
      button: e.button
    })) || 0));
    if (moved <= 0) return false;

    cursorStack.count -= moved;
    if (cursorStack.count <= 0) clearStack(cursorStack);
    postSlotMutation();
    return true;
  }

  function handleInventoryMouseUp(e = null) {
    finalizeCraftLeftDrag();
    stopCraftDragPlace();
    if (e?.currentTarget === inventoryOverlay) {
      tryDropCursorOutside(e);
    }
  }

  inventoryOverlay.addEventListener('mousemove', moveCursor);
  inventoryOverlay.addEventListener('mouseup', handleInventoryMouseUp);
  inventoryOverlay.addEventListener('mouseleave', () => stopCraftDragPlace());
  inventoryOverlay.addEventListener('contextmenu', (e) => e.preventDefault());
  document.addEventListener('mousemove', moveCursor);
  document.addEventListener('mouseup', handleInventoryMouseUp);
  if (inventoryTrashSlotEl) {
    inventoryTrashSlotEl.addEventListener('mouseup', (e) => {
      if (e.button !== 0 && e.button !== 2) return;
      e.preventDefault();
      e.stopPropagation();
      if (isEmptyStack(cursorStack)) return;

      if (e.button === 2) {
        cursorStack.count -= 1;
        if (cursorStack.count <= 0) clearStack(cursorStack);
      } else {
        clearStack(cursorStack);
      }
      postSlotMutation();
    });
    inventoryTrashSlotEl.addEventListener('contextmenu', (e) => e.preventDefault());
  }
  if (inventorySortBtnEl) {
    inventorySortBtnEl.addEventListener('click', () => {
      sortInventoryMain();
    });
  }
  creativePalette.setupSearchInput(() => renderCreativePalette());

  setupSlotDom();
  creativePalette.setupDom();
  ensureSharedOverlayLayout();
  updateModeUi();
  updateInventoryUI();

  onLanguageChange(() => {
    updateModeUi();
    updateInventoryUI();
  });

  return {
    inventory,
    exportState,
    importState,
    setActiveSlot,
    getHotbarSize() {
      return HOTBAR_SIZE;
    },
    getActiveItemId,
    getActiveItemCount,
    getActiveItemDef,
    addItem,
    removeItem,
    dropAllItems,
    dropActiveItem,
    consumeActiveItem,
    hasItem,
    getInsertCapacity: getInventoryInsertCapacity,
    assignItemToHotbar,
    damageActiveTool,
    tryEquipActiveArmor,
    getArmorPoints,
    getArmorReduction,
    damageEquippedArmor,
    setCraftOpen,
    toggleCraftOpen,
    isCraftOpen,
    setCreativeMode,
    isCreativeMode() {
      return creativeMode;
    },
    setTableAccess,
    setCraftMode,
    getCraftMode,
    hasCursorItem,
    getCursorItemId,
    moveExternalSlotToInventory,
    placeOneIntoExternalSlot,
    handleExternalSlotMouseDown,
    handleExternalOutputSlotMouseDown,
    updateInventoryUI,
    refreshLocale() {
      updateModeUi();
      updateInventoryUI();
    }
  };
}
